import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Flyer from '../features/flyers/Flyer';

/**
 * Public Flyer Preview Page
 *
 * Renders a flyer read-only by ID. The route is inside the app's Auth0Provider
 * and NotificationProvider, so all hooks work. Header/Footer are hidden via
 * the isFlyerPreview check in App.jsx.
 *
 * URL: /flyer-preview/:flyerId
 * Query params:
 *   ?export=true      — legacy Puppeteer screenshot mode (5x scale)
 *   ?autoExport=true   — triggers Konva-based export, puts base64 data on window.__exportResult
 */
function FlyerPreview() {
  const { flyerId } = useParams();
  const [flyer, setFlyer] = useState(null);
  const [error, setError] = useState(null);
  const flyerRef = useRef(null);
  const exportTriggered = useRef(false);

  useEffect(() => {
    if (!flyerId) return;

    axios.get(
      `${process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:3042'}/api/flyers/${flyerId}`
    ).then(res => setFlyer(res.data))
     .catch(() => setError('Flyer not found'));
  }, [flyerId]);

  const params = new URLSearchParams(window.location.search);
  const isExport = params.get('export') === 'true';
  const isAutoExport = params.get('autoExport') === 'true';

  // Auto-export: wait for Flyer to mount, then call exportForServer
  useEffect(() => {
    if (!isAutoExport || !flyer || exportTriggered.current) return;

    // Poll until flyerRef is ready and exportForServer is available
    const interval = setInterval(async () => {
      if (!flyerRef.current?.exportForServer) return;

      exportTriggered.current = true;
      clearInterval(interval);

      try {
        window.__exportResult = { status: 'running' };
        const result = await flyerRef.current.exportForServer();
        window.__exportResult = { status: 'done', data: result };
      } catch (err) {
        console.error('Auto-export failed:', err);
        window.__exportResult = { status: 'error', error: err.message };
      }
    }, 500);

    return () => clearInterval(interval);
  }, [isAutoExport, flyer]);

  if (error) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#999', fontFamily: 'Arial' }}>{error}</div>;
  }

  if (!flyer) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#999', fontFamily: 'Arial' }}>Loading...</div>;
  }

  return (
    <div
      id="flyer-preview-container"
      style={{
        background: 'white',
        padding: 0,
        margin: 0,
        display: 'flex',
        justifyContent: 'center',
        // For legacy export: use transform scale for Puppeteer screenshots
        // For autoExport or normal viewing: no scale needed (Konva renders at full res internally)
        ...(isExport && !isAutoExport
          ? { transform: 'scale(5)', transformOrigin: 'top left' }
          : { zoom: 2 }
        ),
      }}
    >
      <Flyer
        ref={flyerRef}
        createdFlyer={flyer}
        setCreatedFlyer={() => {}}
      />
    </div>
  );
}

export default FlyerPreview;
